import StyledAutocomplete from '@components/styled-autocomplete';
import { TypeReturn } from '@components/styled-autocomplete/useStyledAutocomplete';
import { cameraViewController } from '@controllers/index';
import { ListProps } from '@Core';
import { IGetPhotoSnapShotByPTZ } from '@EcamModel/controllers/ICameraViewHttpController';
import { CameraViewType, ViewPreset, ViewPTZ } from '@EcamModel/model/CameraView';
import { LoadingButton } from '@mui/lab';
import { Button, Skeleton, Stack, Typography } from '@mui/material';
import { PREVIEW_RATIO, useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import { Filter } from '@pages/cameras/list';
import ListNoData from '@pages/cameras/list/components/NoDataCameras';
import color from '@theme/Colors';
import Konva from 'konva';
import { useEffect, useRef, useState } from 'react';
import { Image, Layer, Stage } from 'react-konva';
import { base64ToHTMLImageElement, PREVIEW_WIDTH } from './PopupConfigViewCameraPTZ';

type Props = {
    filter: Filter;
    listsViewPresetAutocomplete: TypeReturn<ViewPreset, ListProps<ViewPreset>>;
    listsViewPreset: ViewPreset[];
    onSetFilter: (filter: Filter | ((prev: Filter) => Filter)) => void;
    onCancel: () => void;
    viewPtz?: {
        PtzPanPos: string;
        PtzTiltPos: string;
        PtzZoomPos: string;
    };
};

export default function UpsertView360(props: Props) {
    const { filter, listsViewPreset, listsViewPresetAutocomplete, onSetFilter, onCancel, viewPtz } = props;
    const {
        handleUpsertView,
        filteredCamera,
        isLoadingSave,
        photoSnapShotByPTZ,
        setPhotoSnapShotByPTZ,
        selectedCameraView,
        setSelectedCameraView,
        listCameraView,
    } = useCameraDetailContext();

    const stageRef = useRef<Konva.Stage>(null);
    const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null);
    const initialPerspectiveViewRef = useRef<Partial<ViewPTZ> | null>(null);
    const shouldSkipApiCall = useRef<Partial<ViewPTZ> | boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const cameraViewName = selectedCameraView?.CameraViewName;

    const scaleSize = PREVIEW_WIDTH / (imageElement?.width ?? 1920);

    useEffect(() => {
        if (photoSnapShotByPTZ) {
            const convertBase64ToImage = async () => {
                try {
                    const imgElement = await base64ToHTMLImageElement(photoSnapShotByPTZ.Base64PhotoSnapShot);
                    setImageElement(imgElement);
                } catch (error) {
                    console.error('Failed to convert base64 to image', error);
                }
            };
            convertBase64ToImage();
        }
    }, [photoSnapShotByPTZ]);

    const handleResetFilter = () => {
        const newPerspectiveView: Partial<ViewPreset> = {
            GotoPointId: viewPtz ? undefined : listsViewPreset?.[0]?.GotoPointId,
            PtzPanPos: viewPtz?.PtzPanPos || listsViewPreset?.[0]?.PtzPanPos,
            PtzTiltPos: viewPtz?.PtzTiltPos || listsViewPreset?.[0]?.PtzTiltPos,
            PtzZoomPos: viewPtz?.PtzZoomPos || listsViewPreset?.[0]?.PtzZoomPos,
            PresetName: viewPtz ? cameraViewName : listsViewPreset?.[0]?.PresetName,
        };

        onSetFilter((prev) => ({
            ...prev,
            perspectiveView: newPerspectiveView,
        }));

        if (!initialPerspectiveViewRef.current) {
            initialPerspectiveViewRef.current = newPerspectiveView;
        }
    };

    useEffect(() => {
        handleResetFilter();

        shouldSkipApiCall.current = true;
    }, []);

    useEffect(() => {
        if (!initialPerspectiveViewRef.current) return;

        if (shouldSkipApiCall.current) {
            shouldSkipApiCall.current = false;
            return;
        }

        const PTZ =
            filter.perspectiveView?.PtzPanPos !== undefined ||
            filter.perspectiveView?.PtzZoomPos !== undefined ||
            filter.perspectiveView?.PtzTiltPos !== undefined
                ? {
                      PtzPanPos: filter.perspectiveView?.PtzPanPos,
                      PtzZoomPos: filter.perspectiveView?.PtzZoomPos,
                      PtzTiltPos: filter.perspectiveView?.PtzTiltPos,
                  }
                : undefined;

        if (PTZ || filter.perspectiveView?.GotoPointId !== undefined) {
            getPhotoSnapShotByView({
                PTZ:
                    filter.perspectiveView?.GotoPointId !== undefined
                        ? {
                              GotoPointId: filter.perspectiveView?.GotoPointId,
                          }
                        : PTZ!,
                PTZCommand: filteredCamera,
            });
        }
    }, [filter.perspectiveView]);

    const getPhotoSnapShotByView = async (_filter: IGetPhotoSnapShotByPTZ) => {
        setIsLoading(true);
        await cameraViewController
            .getPhotoSnapShotByPTZ(_filter)
            .then((res) => {
                setPhotoSnapShotByPTZ(res);
            })

            .finally(() => {
                setIsLoading(false);
            });
    };

    const isViewOverview = selectedCameraView?.ViewType === CameraViewType.ViewOverview;

    const conditionDisabled =
        isLoadingSave || isLoading || !imageElement || filter.perspectiveView?.GotoPointId === undefined;

    return (
        <Stack direction={'column'} spacing={2} width={'100%'}>
            <Stack direction={'row'} spacing={4} alignItems={'center'}>
                <Typography>
                    {viewPtz ? 'Update' : 'Add new'} {viewPtz && !isViewOverview ? 'View' : 'overview'}:
                </Typography>
                <Stack flex={1}>
                    <StyledAutocomplete
                        {...listsViewPresetAutocomplete}
                        getOptionLabel={(o) => o.PresetName ?? ''}
                        label="View"
                        isRequired
                        disabled={isLoading}
                        value={filter.perspectiveView}
                        disabledAllOption
                        onChange={(o) => {
                            onSetFilter((prev) => ({ ...prev, perspectiveView: o }));
                        }}
                    />
                </Stack>

                <Stack direction={'row'} spacing={1} alignItems={'center'}>
                    <LoadingButton
                        sx={{
                            minWidth: 80,
                        }}
                        size="small"
                        loading={isLoadingSave}
                        loadingPosition="center"
                        variant="contained"
                        disabled={conditionDisabled}
                        onClick={async () => {
                            await handleUpsertView(
                                filter,
                                viewPtz
                                    ? isViewOverview
                                        ? CameraViewType.ViewOverview
                                        : CameraViewType.ViewDetail
                                    : CameraViewType.ViewOverview,
                                viewPtz ? selectedCameraView?.Id : undefined
                            );

                            onCancel();
                        }}
                    >
                        Save
                    </LoadingButton>
                    <Button
                        sx={{ minWidth: 80 }}
                        size="small"
                        variant="outlined"
                        onClick={() => {
                            onCancel();
                            !selectedCameraView && setSelectedCameraView(listCameraView?.rows?.[0]);
                        }}
                        disabled={isLoadingSave}
                    >
                        Cancel
                    </Button>
                </Stack>
            </Stack>

            {filter.perspectiveView?.GotoPointId !== undefined || filter.perspectiveView?.PtzPanPos !== undefined ? (
                <Stack alignItems={'center'}>
                    {!imageElement || isLoading ? (
                        <Skeleton
                            width="100%"
                            sx={{
                                borderRadius: '4px',
                                bgcolor: color.grey200,
                                minHeight: PREVIEW_WIDTH * PREVIEW_RATIO,
                            }}
                            variant="rectangular"
                        />
                    ) : (
                        <Stage ref={stageRef} width={PREVIEW_WIDTH} height={PREVIEW_WIDTH * PREVIEW_RATIO}>
                            <Layer>
                                <Image image={imageElement} scaleX={scaleSize} scaleY={scaleSize} />
                            </Layer>
                        </Stage>
                    )}
                </Stack>
            ) : (
                <ListNoData
                    sx={{
                        minHeight: '300px',
                        backgroundColor: color.white,
                        border: 'none',
                    }}
                />
            )}
        </Stack>
    );
}
